/** @jsx jsx */
import { css, jsx, Theme } from '@emotion/react'
import { MouseEvent, ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Backdrop, IconButton, Stack, Tooltip } from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
  FileDownload as FileDownloadIcon,
  FileDownloadDone as FileDownloadDoneIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { AudioPlayer, Button, ButtonMenu, Divider, MenuItem } from '@vendorflow/components'
import { last, sortBy, uniqBy } from 'lodash'
import { AxiosError } from 'axios'
import JSZip from 'jszip'
import FileSaver from 'file-saver'
import {
  convertAssetsToPdf,
  downloadAssetToMachine,
  downloadFile,
  downloadFiles,
  getAssetsByPDFCompatibleCategory,
  isImage,
} from 'services/Files.services'
import AssetThumbnail from 'components/AssetThumbnail'
import DocumentDownloadButton from './ChannelView/MessageRenderers/Reusables/DocumentDownloadButton'
import { WarningMessage } from 'components/WarningMessage'
import { isIOS } from 'services/Utils.service'
import { useLightboxContext } from 'features/lightbox/LightboxProvider'
import ZoomPdfViewer from 'components/PdfViewer/ZoomPdfViewer'
import { ManagedReplyContext } from 'queries/ReplyContext.queries'
import { v4 as uuidv4 } from 'uuid'
import { useFirebaseContext } from 'features/firebase/FirebaseProvider'
import { useMyIdentityId } from 'queries/Auth.queries'
import { getAttachmentDownloadedPath } from 'features/firebase/Firebase.services'
import { getAttachmentRefId, getUseDownloadedAttachmentHrefQueryKey } from 'queries/Attachments.queries'
import { useIsOnMobile } from './Workspace.service'
import { Maybe } from '@vendorflow/types'
import { useNotificationsContext } from 'features/notifications/NotificationsProvider'
import { MediaAsset } from 'pages/WorkspacePage/ChannelView/ChannelInterfaceProvider/ChannelInterface.types'
import { useDownloadAsTiffEnabled } from 'features/feature-flags/FeatureFlag.hooks'
import { convertImageToTiff } from 'services/Images.service'
import { useQueryClient } from '@tanstack/react-query'
import { Logger } from 'features/logging/Logging.services'

const ARROW_CONTAINER_WIDTH = 56
const ACTION_BAR_HEIGHT = 40
const MARGIN_IN_PIXELS = 8
const FILM_STRIP_HEIGHT = 120 + MARGIN_IN_PIXELS * 2

const mediaItemStyle = css`
  height: 100%;
  width: 100%;
  object-fit: contain;
`
const whiteIconStyle = (theme: Theme) => css`
  color: white;

  &:hover {
    background-color: ${theme.custom.palette.grey['33']};
  }
`

const arrowIconStyle = css`
  font-size: 40px;
`

export default function Lightbox() {
  const { open, selected: initialSelected, ...lightbox } = useLightboxContext()
  const isMobile = useIsOnMobile()
  const assets = useMemo(() => sortBy(lightbox.assets, 'timestamp'), [lightbox.assets])
  const [selected, setSelected] = useState(0)
  const selectedAsset = assets.length ? assets[selected] : null
  const hasMultiple = assets.length > 1
  const warningMessageText = `3GP format is not currently supported. Please download ${
    isMobile ? (isIOS ? '' : 'from the desktop app ') : 'to the desktop '
  }and play offline.`

  useEffect(() => {
    if (!open) {
      setSelected(0)
      window.removeEventListener('keydown', handleKeyDown)
    } else if (open && hasMultiple) {
      setSelected(initialSelected)
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  function handleKeyDown(evt: KeyboardEvent) {
    if (evt.key === 'ArrowRight') {
      setSelected((val) => (val < assets.length - 1 ? val + 1 : val))
    } else if (evt.key === 'ArrowLeft') {
      setSelected((val) => (val > 0 ? val - 1 : val))
    }
  }

  function handleClose() {
    lightbox.setLightboxOpen(false)
    lightbox.setLightboxAssets([])
  }

  function slidePrev(evt: MouseEvent) {
    evt.stopPropagation()

    if (selected > 0) {
      setSelected((prev) => prev - 1)
    }
  }

  function slideNext(evt: MouseEvent) {
    evt.stopPropagation()

    if (selected < assets.length - 1) {
      setSelected((prev) => prev + 1)
    }
  }

  function renderContent() {
    if (!selectedAsset || !selectedAsset.mimeType || !selectedAsset.url) {
      return null
    }

    if (selectedAsset.mimeType.includes('3gpp')) {
      return (
        <div>
          {isMobile && isIOS && (
            <div
              css={css`
                display: flex;
                justify-content: center;
              `}
            >
              <DownloadMenu assets={[selectedAsset]} selected={0} />
            </div>
          )}
          <WarningMessage>{warningMessageText}</WarningMessage>
        </div>
      )
    }

    if (selectedAsset.mimeType.includes('image')) {
      return <img crossOrigin="anonymous" css={mediaItemStyle} src={selectedAsset.url} alt={selectedAsset.filename} />
    }

    if (selectedAsset.mimeType.includes('audio')) {
      return !isIOS ? (
        <AudioPlayer crossOrigin="anonymous" src={selectedAsset.url} />
      ) : (
        <div>
          <DocumentDownloadButton href={selectedAsset.url} filename={selectedAsset.filename!} />
          <WarningMessage>
            This audio note is not yet supported on iOS. Please click to download and play the audio with an external
            player, or use the desktop browser to access the audio.
          </WarningMessage>
        </div>
      )
    }

    if (selectedAsset.mimeType.includes('video')) {
      return <video crossOrigin="anonymous" css={mediaItemStyle} src={selectedAsset.url} controls />
    }

    if (selectedAsset.mimeType === 'application/pdf') {
      return (
        <div
          css={css`
            height: 100%;
            width: 100%;

            & svg {
              stroke: white;
            }

            & .rpv-zoom__popover-target {
              color: white;
            }

            & .rpv-zoom__popover-target-arrow {
              border-color: white transparent transparent;
            }

            & .zoom-pdf-viewer__current-page {
              color: white;
            }
          `}
        >
          <ZoomPdfViewer src={selectedAsset.url} />
        </div>
      )
    }
  }

  if (!selectedAsset || !selectedAsset.url) {
    return null
  }

  return (
    <Backdrop
      css={css`
        z-index: 1400;
        background-color: rgba(0, 0, 0, 0.94);
      `}
      open={open}
    >
      <div
        css={css`
          height: 100vh;
          display: grid;
          grid-template-rows: ${ACTION_BAR_HEIGHT}px calc(100vh - ${ACTION_BAR_HEIGHT + FILM_STRIP_HEIGHT}px) ${FILM_STRIP_HEIGHT}px;
        `}
      >
        <ActionBar assets={assets} selected={selected} handleClose={handleClose} />
        <Stack
          css={css`
            height: 100%;
            width: 100vw;
          `}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <div>
            <IconButton
              css={(theme: Theme) => css`
                ${whiteIconStyle(theme)};
                visibility: ${hasMultiple && selected > 0 ? 'visible' : 'hidden'};
              `}
              color="default"
              onClick={slidePrev}
            >
              <ArrowBackIcon css={arrowIconStyle} />
            </IconButton>
          </div>
          <Stack
            css={css`
              margin: 0.5rem;
              height: 100%;
              width: calc(100% - ${ARROW_CONTAINER_WIDTH * 2}px);
            `}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            {renderContent()}
          </Stack>
          <div>
            <IconButton
              css={(theme: Theme) => css`
                ${whiteIconStyle(theme)};
                visibility: ${hasMultiple && selected < assets.length - 1 ? 'visible' : 'hidden'};
              `}
              color="default"
              onClick={slideNext}
            >
              <ArrowForwardIcon css={arrowIconStyle} />
            </IconButton>
          </div>
        </Stack>
        <FilmStrip assets={assets} selected={selected} setSelected={setSelected} />
      </div>
    </Backdrop>
  )
}

function ActionBar({
  assets,
  selected,
  handleClose,
}: {
  assets: MediaAsset[]
  selected: number
  handleClose: () => void
}) {
  const selectedAsset = assets.length ? assets[selected] : null
  if (!selectedAsset || !selectedAsset.url) {
    return null
  }
  const isMobile = useIsOnMobile()

  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="center">
      <Stack
        css={css`
          box-sizing: border-box;
          padding-left: 40px;
          width: calc(100vw - 40px);
        `}
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        {!isMobile && <DownloadMenu assets={assets} selected={selected} disableSingles />}
        <a href={selectedAsset.url} target="_blank" rel="noopener noreferrer">
          <IconButton css={whiteIconStyle}>{<OpenInNewIcon />}</IconButton>
        </a>
      </Stack>
      <IconButton css={whiteIconStyle} onClick={handleClose}>
        <CloseIcon />
      </IconButton>
    </Stack>
  )
}

function FilmStrip({
  assets,
  selected,
  setSelected,
}: {
  assets: MediaAsset[]
  selected: number
  setSelected: (selected: number) => void
}) {
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(6)
  const displayedAssets = useMemo(() => assets.slice(start, end + 1), [assets, selected, start, end])

  useLayoutEffect(() => {
    if (selected === 0 || selected === assets.length - 1) {
      return
    }
    if (selected < start + 1) {
      const newStart = selected - 1
      setStart(newStart)
      setEnd(newStart + 6)
    } else if (selected > end - 1) {
      const newEnd = selected + 1
      setStart(newEnd - 6)
      setEnd(newEnd)
    }
  }, [selected])

  return (
    <Stack
      css={css`
        width: 100%;
        height: ${FILM_STRIP_HEIGHT - MARGIN_IN_PIXELS * 2}px;
        margin: 0.5rem;
      `}
      direction="row"
      justifyContent="center"
      alignItems="center"
    >
      {displayedAssets.map((asset, index) => {
        const trueIndex = index + start
        return (
          <Button
            key={asset.url!}
            css={css`
              background: transparent;
              box-shadow: none;
              border-radius: 0;
              padding: 0;
              margin: 0 0.25rem;
              border: 3px solid ${selected === trueIndex ? 'white' : 'transparent'};
              opacity: ${selected === trueIndex ? 1 : trueIndex >= start && trueIndex <= end ? 0.5 : 0};
              transition: opacity 0.25s;

              &:hover {
                background: transparent;
              }
            `}
            onClick={() => setSelected(trueIndex)}
          >
            <AssetThumbnail attachment={asset} />
          </Button>
        )
      })}
    </Stack>
  )
}

export function DownloadMenu({
  assets,
  selected,
  disableSingles,
  disabled,
  replyContexts,
  buttonRenderer,
  onDownloadSuccess,
}: {
  assets: MediaAsset[]
  selected: number
  disableSingles?: boolean
  disabled?: boolean
  replyContexts?: ManagedReplyContext[]
  buttonRenderer?: (downloadedTimestamp: Maybe<number>) => ReactNode
  onDownloadSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const DOWNLOAD_AS_TIFF_ENABLED = useDownloadAsTiffEnabled()
  const { enqueueSnackbarError } = useNotificationsContext()
  const { alice } = useLightboxContext()
  const { useFirebaseData, setFirebaseData } = useFirebaseContext()
  const bobId = useMyIdentityId()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const selectedAsset = assets.length ? assets[selected] : null
  const selectedAssetRef = selectedAsset?.ref && getAttachmentRefId(selectedAsset?.ref)
  const { data: downloadedRef } = useFirebaseData<number>(getAttachmentDownloadedPath(selectedAssetRef, bobId))
  const isAnyDownloadableAsPdf = useMemo(() => assets.some((asset) => isImage(asset)), [assets])
  const isImageFile = useMemo(() => (selectedAsset ? isImage(selectedAsset) : false), [selectedAsset])
  const isSelectedDownloadableAsPdf = isImageFile
  const isOnlyOneAsset = useMemo(() => assets.length === 1, [assets])
  const isMultipleAssets = useMemo(() => assets.length > 1, [assets])
  const alices = replyContexts?.map((rc) => rc.relationships.recipient!) || []
  const isAllAlicesUnique = useMemo(() => {
    const uniqueAlices = uniqBy(alices, 'id')
    return uniqueAlices.length === alices.length
  }, [alices])
  const isAllFilenamesUnique = useMemo(() => {
    const filenames = assets.map((asset) => asset.filename)
    const uniqueFilenames = uniqBy(assets, 'filename')

    return filenames.length === uniqueFilenames.length
  }, [assets])
  const selectedAssetId = assets[selected]?.ref && getAttachmentRefId(assets[selected].ref)
  const assetIds = assets.map((asset) => {
    return getAttachmentRefId(asset?.ref)
  })
  const menuItems: (MenuItem | Divider)[] = useMemo(() => {
    const items: (MenuItem | Divider)[] = [
      {
        content: 'Download',
        onClick: () => handleDownload('single'),
        type: 'MenuItem',
        disabled: !disableSingles && isMultipleAssets,
      },
      {
        content: 'Download as PDF',
        onClick: () => handleDownload('single-pdf'),
        type: 'MenuItem',
        disabled: !isSelectedDownloadableAsPdf || (!disableSingles && isMultipleAssets),
      },
      {
        text: '',
        textAlign: 'center',
        type: 'Divider',
      },
      {
        content: 'Download All as Zip',
        onClick: () => handleDownload('all-zip'),
        type: 'MenuItem',
        disabled: isOnlyOneAsset,
      },
      {
        content: 'Download All as PDF',
        onClick: () => handleDownload('all-pdf'),
        type: 'MenuItem',
        disabled: !isAnyDownloadableAsPdf || isOnlyOneAsset,
      },
    ]

    if (DOWNLOAD_AS_TIFF_ENABLED) {
      items.splice(1, 0, {
        content: 'Download as TIFF',
        onClick: () => handleDownload('single-tiff'),
        type: 'MenuItem',
        disabled: (!disableSingles && isMultipleAssets) || !isImageFile,
      })
    }

    return items
  }, [isSelectedDownloadableAsPdf, isAnyDownloadableAsPdf, isOnlyOneAsset, selectedAsset, assets, selected])

  async function handleDownload(mode: 'single' | 'single-pdf' | 'single-tiff' | 'all-zip' | 'all-pdf') {
    setIsLoading(true)
    try {
      switch (mode) {
        case 'single':
          await handleDownloadFile()
          break
        case 'single-pdf':
          await handleDownloadFilesAsPdf(true)
          break
        case 'single-tiff':
          await handleDownloadFileAsTiff()
          break
        case 'all-zip':
          await handleZipDownload()
          break
        case 'all-pdf':
          await handleDownloadFilesAsPdf()
          break
        default:
          break
      }
      void Promise.allSettled(
        assets.map(
          async (asset) => await queryClient.invalidateQueries(getUseDownloadedAttachmentHrefQueryKey(asset.ref))
        )
      )

      if (onDownloadSuccess) {
        onDownloadSuccess()
      }
    } catch (e) {
      const status = (e as AxiosError)?.response?.status
      if (status === 403 || status === 401) {
        enqueueSnackbarError({
          message: `Unable to download your file(s) because one has expired and is no longer accessible.`,
        })
      } else {
        Logger.error(e)
        enqueueSnackbarError({ message: `Unable to download your file(s) because of an unexpected error.` })
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleDownloadFile() {
    const now = getCurrentTime()
    if (!selectedAsset) {
      throw new Error('No asset selected')
    }
    if (!selectedAsset.url) {
      throw new Error('No url for selected asset')
    }
    if (!selectedAsset.filename) {
      throw new Error('No filename for selected asset')
    }
    const blob = await downloadFile(selectedAsset.url)
    downloadAssetToMachine(blob, renameFile(selectedAsset.filename, 0))
    await setFirebaseData(getAttachmentDownloadedPath(selectedAssetId, bobId), now)
  }

  async function handleDownloadFilesAsPdf(single?: boolean) {
    const now = getCurrentTime()
    if (!selectedAsset) {
      throw new Error('No asset selected')
    }
    const result = await convertAssetsToPdf(single ? [selectedAsset] : assets)
    const filename = single
      ? selectedAsset.filename
        ? renameFile(selectedAsset.filename!, 0, 'pdf')
        : 'Document.pdf'
      : 'Documents.pdf'
    const { pdfs, images } = getAssetsByPDFCompatibleCategory(assets)
    const groupedAssets = (pdfs || []).concat(images)
    downloadAssetToMachine(result, filename)
    if (single) {
      void setFirebaseData(getAttachmentDownloadedPath(selectedAssetId, bobId), now)
    } else {
      groupedAssets.forEach((asset) => {
        void setFirebaseData(getAttachmentDownloadedPath(getAttachmentRefId(asset.ref), bobId), now)
      })
    }
  }

  async function handleDownloadFileAsTiff() {
    const now = getCurrentTime()
    if (!selectedAsset) {
      throw new Error('No asset selected')
    }
    if (!selectedAsset.url) {
      throw new Error('No url for selected asset')
    }
    if (!selectedAsset.filename) {
      throw new Error('No filename for selected asset')
    }

    const blob = await convertImageToTiff(selectedAsset.url)
    downloadAssetToMachine(blob, renameFile(selectedAsset.filename!, 0, 'tiff'))
    await setFirebaseData(getAttachmentDownloadedPath(selectedAssetId, bobId), now)
  }

  async function handleZipDownload() {
    const now = getCurrentTime()
    const zip = new JSZip()
    const urls = assets.map((asset) => asset.url!)
    const results = await downloadFiles(urls)
    results.forEach((asset, index) => {
      return zip.file(renameFile(assets[index].filename!, index), results[index])
    })
    const zipped = await zip.generateAsync({ type: 'blob' })
    FileSaver.saveAs(zipped, 'Documents.zip')
    assetIds.forEach((id) => {
      void setFirebaseData(getAttachmentDownloadedPath(id, bobId), now)
    })
  }

  function renameFile(filename: string, index: number, extension?: string) {
    const selectedAlice = alices[index] || alice || null
    const fileNameParts: string[] = filename.split('.')
    const fileNameWithoutExtension = fileNameParts.slice(0, -1).join('_')
    const fileExtension = extension || last(fileNameParts)
    const firstName = selectedAlice?.firstName
    const lastName = selectedAlice?.lastName

    return `${fileNameWithoutExtension}${firstName ? '_' + firstName : ''}${lastName ? '_' + lastName : ''}${
      !isAllAlicesUnique && !isAllFilenamesUnique ? '_' + uuidv4() : ''
    }.${fileExtension}`
  }

  function getCurrentTime() {
    return Math.floor(new Date().getTime() / 1000)
  }

  return (
    <ButtonMenu
      css={css`
        z-index: 1400;
      `}
      id="download-menu"
      buttonRenderer={
        buttonRenderer ? (
          buttonRenderer(downloadedRef)
        ) : (
          <Tooltip title="Download">
            <IconButton css={whiteIconStyle} data-testid="download-menu-button">
              {downloadedRef ? <FileDownloadDoneIcon /> : <FileDownloadIcon />}
            </IconButton>
          </Tooltip>
        )
      }
      disabled={disabled || isLoading}
      menuItems={menuItems}
    />
  )
}
